import React, { useContext, useEffect, useState } from "react";
import ErrorMessage from "@/components/elements/global/ErrorMessage";
import DatePicker from "@/components/elements/global/DatePicker";
import BasicTimePicker from "@/components/elements/global/BasicTimePicker";
import { formatDateStringToTime, formatDateToNearestHour } from "@/helpers/Dates";
import {IPublishedDetails, ScheduleType,} from "@/interfaces/ScheduledNotifications";
import { SchedulePushNotificationContext } from "@/context/SchedulePushNotificationContext";


const ScheduleStep = ({setStepTwoValid, publishDetails, hasMetNotificationLimit}: { setStepTwoValid: (val: boolean) => void; publishDetails: IPublishedDetails; hasMetNotificationLimit: boolean}) => {
  const { store, setStore } = useContext(SchedulePushNotificationContext);
  const [isStoreDataLoaded, setIsStoreDataLoaded] = useState<boolean>(false);

  //variables for error message handling
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorMessageVariables, setErrorMessageVariables] = useState<{message: string; className: string}>({ message: "", className: "" });
  const [hasDateSelectionError, setHasDateSelectionError] = useState<boolean>(false);

  const formattedScheduledPublishTime = () => {
    if (publishDetails.scheduledPublishDate) {
      const date = new Date(publishDetails.scheduledPublishDate);
      const options: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "long",
        hour: "numeric",
        minute: "numeric",
      };
      return new Intl.DateTimeFormat(undefined, options).format(date);
    } else {
      return null;
    }
  };
  const formattedScheduledArchiveDate = () => {
    if (publishDetails.scheduledArchiveDate) {
      const date = new Date(publishDetails.scheduledArchiveDate);
      const options: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
      };
      return new Intl.DateTimeFormat(undefined, options).format(date);
    } else {
      return null;
    }
  };
  const formattedScheduledPublishTimeForRadioOption = formattedScheduledPublishTime();
  const formattedScheduledArchiveDateForErrorMessage = formattedScheduledArchiveDate();

  //variables for radio button logic handling
  const [radioSelect, setRadio] = useState<ScheduleType | null>(null);
  const [radioScheduledPublishCopy, setRadioScheduledPublishCopy] = useState<string>("Send with set Scheduled Publish time");
  const [disableScheduledPublish, setDisableScheduledPublish] = useState<boolean>();

  //variables for custom date selection
  const [defaultMinCalendarDate, setDefaultMinCalendarDate] = useState<Date>();
  const [defaultTime, setDefaultTime] = useState<string>("");
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>();
  const [customDateTime, setCustomDateTime] = useState<string>();
  const [customDate, setCustomDate] = useState<Date>();
  const [customTime, setCustomTime] = useState<string>();
  const [isDefault, setDefault] = useState<boolean>(false);

  //on initial load, we ensure the store data is available
  useEffect(() => {
    if (publishDetails && !isStoreDataLoaded) {
      setIsStoreDataLoaded(true);
      setShowErrorMessage(false);
    } else if (!publishDetails) {
      setErrorMessageVariables({
        message: "Something went wrong. Please refresh the page and try again.",
        className: "something-went-wrong-error",
      });
      setShowErrorMessage(true);
    }
  }, []);

  //we can set the validity of the form and handling the radio selection
  useEffect(() => {
    handleScheduledPublishRadioState();
  }, [isStoreDataLoaded]);

  //this determines if the "send with scheduled publish time option" is available
  const handleScheduledPublishRadioState = () => {
    //if the publish date is in the past and the content is published, or if there is already a scheduled publish notification, disable the button
    if (
      checkIfContentPublishDateIsPast() ||
      (!checkIfThisIsSchedulePushNotification() &&
        checkIfSchedulePushNotificationExists())
    ) {
      setDisableScheduledPublish(true);
    } else {
      setDisableScheduledPublish(false);
      if (publishDetails.scheduledPublishDate) {
        setRadioScheduledPublishCopy("Send with set Scheduled Publish time (" + formattedScheduledPublishTimeForRadioOption + ")?");
      }
    }
  };

  //if the limit is met, the only option available to the user is to send now, and other options are removed
  useEffect(() => {
    if (hasMetNotificationLimit) {
      handleNowRadioSelection();
    } else if (store.scheduledPushTime || store.scheduleType) {
      handleRadioSelectionOnLoad();
    }
  }, [disableScheduledPublish]);

  //this will execute once on load of state data
  const handleRadioSelectionOnLoad = () => {
    clearErrorMessages();

    if (store.scheduleType) {
      switch (store.scheduleType) {
        case ScheduleType.NOW:
          handleNowRadioSelection();
          break;
        case ScheduleType.PUBLISH:
          handlePublishRadioSelection();
          break;
        case ScheduleType.CHOOSE:
          //if it is the same time as Scheduled publish we set to Schedule Publish option
          if (store.scheduledPushTime && checkIfTimeMatchesSchedulePublish()) {
            handlePublishRadioSelection();
          } else {
            handleChooseRadioSelection();
          }
          break;
      }
      //if it is the same time as Scheduled publish we set to Schedule Publish option
    } else if (store.scheduledPushTime) {
      if (checkIfTimeMatchesSchedulePublish()) {
        handlePublishRadioSelection();
      } else {
        handleChooseRadioSelection();
      }
    }
  };

  //this will execute everytime a date is selected
  const handleRadioSelection = (event: any) => {
    event.persist();
    clearErrorMessages();

    const radioSelection = event.target.value;
    switch (radioSelection) {
      case ScheduleType.NOW:
        handleNowRadioSelection();
        break;
      case ScheduleType.PUBLISH:
        handlePublishRadioSelection();
        break;
      case ScheduleType.CHOOSE:
        handleChooseRadioSelection();
        break;
    }
  };

  function handleNowRadioSelection() {
    setStoreDetails(ScheduleType.NOW, "NOW");
    setRadio(ScheduleType.NOW);
  }

  function handlePublishRadioSelection() {
    setStoreDetails(ScheduleType.PUBLISH, publishDetails.scheduledPublishDate);
    setRadio(ScheduleType.PUBLISH);
  }

  function handleChooseRadioSelection() {
    setRadio(ScheduleType.CHOOSE);
    setDefaultCalendarValues();
  }

  //when selecting Choose, we have to set the minimum values of the date and time pickers
  function setDefaultCalendarValues() {
    setDefault(true);
    if (store.scheduledPushTime) {
      //if the current notification is scheduled with publish, we set the default to match the publication datetime
      if (
        checkIfTimeMatchesSchedulePublish() ||
        store.scheduleType === ScheduleType.PUBLISH
      ) {
        //if the schedule publish selection has been made, but the publish time has passed, we can default to now.
        if (checkIfContentPublishDateIsPast()) {
          createDefaultCurrentTimePicker();
        } else {
          createDefaultScheduledPublishDateAndTimePicker();
        }
        //if it is for now, set the datetime to now
      } else if (store.scheduleType === ScheduleType.NOW) {
        createDefaultCurrentTimePicker();

        //if it is not a selected radio option, or scheduled with publication, we set the default to chosendatetime
      } else if (
        !checkIfTimeMatchesSchedulePublish() ||
        store.scheduleType === ScheduleType.CHOOSE
      ) {
        createDefaultChosenDateAndTimePicker();

        //if a schedule publish option exists, and the content is not yet published, default closest hour after publication
      } else if (
        (!publishDetails.isPublished || isRepublishingContentInFuture()) &&
        !checkIfTimeMatchesSchedulePublish() &&
        checkIfSchedulePushNotificationExists()
      ) {
        createDefaultNearestHourScheduledPublishDateAndTimePicker();

        //if the content is already publish and has no future publish dates
      } else if (publishDetails.isPublished) {
        createDefaultNearestHourScheduledPublishDateAndTimePicker();
      }

      //if it has been published, or is not yet publish but does not have a publishdate, we can default to today date time we default to current datetimes
    } else if (
      publishDetails.isPublished ||
      checkIfContentPublishDateIsPast()
    ) {
      createDefaultCurrentTimePicker();

      //if it has not been published yet and it has a publish date, (or is published, but has a new schedule publish date), and is without a with scheduled publish notification, we set to match the publish datetime
    } else if (
      (isRepublishingContentInFuture() ||
        (!publishDetails.isPublished && publishDetails.scheduledPublishDate)) &&
      !checkIfSchedulePushNotificationExists()
    ) {
      createDefaultScheduledPublishDateAndTimePicker();
    } else if (
      (isRepublishingContentInFuture() ||
        (!publishDetails.isPublished && publishDetails.scheduledPublishDate)) &&
      checkIfSchedulePushNotificationExists()
    ) {
      createDefaultNearestHourScheduledPublishDateAndTimePicker();
    } else {
      createDefaultCurrentTimePicker();
    }

    setSelectedDateTime();
  }

  function checkIfContentPublishDateIsPast() {
    if (
      new Date(publishDetails.scheduledPublishDate).getTime() <
      new Date().getTime()
    ) {
      return true;
    }
  }

  //Content can be published, but also have a future publish date for any changes made to the content which are not yet published
  function isRepublishingContentInFuture() {
    return publishDetails.isPublished && !checkIfContentPublishDateIsPast();
  }

  function checkIfTimeMatchesSchedulePublish() {
    return (
      new Date(store.scheduledPushTime).getTime() ===
      new Date(publishDetails.scheduledPublishDate).getTime()
    );
  }

  //if this is the schedule push notification we want to default to this option
  function checkIfThisIsSchedulePushNotification() {
    return publishDetails.scheduledNotifications.some((notification) => {
      return (
        new Date(notification.scheduledPushTime).getTime() ===
          new Date(publishDetails.scheduledPublishDate).getTime() &&
        store &&
        store.id === notification.id
      );
    });
  }

  //if a notification exists at the same time as publication, we want to ensure that users cannot use this option
  function checkIfSchedulePushNotificationExists() {
    return publishDetails.scheduledNotifications.some((notification) => {
      return (
        new Date(notification.scheduledPushTime).getTime() ===
        new Date(publishDetails.scheduledPublishDate).getTime()
      );
    });
  }

  //the following four methods help set the default picker values
  const createDefaultCurrentTimePicker = () => {
    const defaultCurrentTime = formatDateToNearestHour(new Date());
    setDefaultTime(defaultCurrentTime + ":00");
    setSelectedTime(defaultCurrentTime + ":00");
    setDefaultMinCalendarDate(new Date());
  };
  function createDefaultScheduledPublishDateAndTimePicker() {
    const defaultPublishTime = formatDateStringToTime(
      publishDetails.scheduledPublishDate
    );
    setDefaultTime(defaultPublishTime);
    setSelectedTime(defaultPublishTime);
    setDefaultMinCalendarDate(new Date(publishDetails.scheduledPublishDate));
  }
  function createDefaultNearestHourScheduledPublishDateAndTimePicker() {
    const defaultSchedulePublishHour = formatDateToNearestHour(
      new Date(publishDetails.scheduledPublishDate)
    );
    setDefaultTime(defaultSchedulePublishHour + ":00");
    setSelectedTime(defaultSchedulePublishHour + ":00");
    setDefaultMinCalendarDate(new Date(publishDetails.scheduledPublishDate));
  }
  function createDefaultChosenDateAndTimePicker() {
    const defaultChosenTime = formatDateStringToTime(store.scheduledPushTime);
    setDefaultTime(defaultChosenTime);
    setSelectedTime(defaultChosenTime);
    setDefaultMinCalendarDate(new Date(store.scheduledPushTime));
  }

  //updating the selected datetime on any date or time selection
  useEffect(() => {
    setSelectedDateTime();
  }, [defaultTime, defaultMinCalendarDate, selectedCalendarDate, selectedTime]);

  function setSelectedDateTime() {
    //if the user has selected a new date or new time, the new values are set, or fallback to default values.
    if (selectedCalendarDate || defaultMinCalendarDate) {
      //setting the date value
      if (selectedCalendarDate && !isDefault) {
        setCustomDate(new Date(selectedCalendarDate));
      } else {
        setCustomDate(new Date(defaultMinCalendarDate));
      }

      //setting the time value
      if (selectedTime) {
        setCustomTime(selectedTime);
      } else {
        setCustomTime(defaultTime);
      }
    }

    setDefault(false);
  }

  //on all changes to either the date or time, the custom datetime is set
  useEffect(() => {
    if (customDate && customTime) {
      createAndSetCustomDateTime(customDate, customTime);
    }
  }, [customDate, customTime]);

  function createAndSetCustomDateTime(customDate: Date, customTime: string) {
    const [hours, minutes] = customTime.split(":");
    customDate.setHours(Number(hours));
    customDate.setMinutes(Number(minutes));
    setCustomDateTime(customDate.toString());
  }

  function validateSelectedDateAndSetErrors(dateTimeCustom: string) {
    clearErrorMessages();

    if (radioSelect === ScheduleType.CHOOSE) {
      //must not be historical datetime
      if (new Date().getTime() > new Date(dateTimeCustom).getTime()) {
        setHasDateSelectionError(true);
        setErrorMessageVariables({
          message: "You must select a date and time in the future",
          className: "invalid-date-time-selection-error",
        });
        return false;
      }

      //if the piece of content is published, we can schedule notifications for anytime, regardless of whether it is due to be republished.
      //it must be after the content is scheduled for publication
      //if there is a publish date
      if (!publishDetails.isPublished && publishDetails.scheduledPublishDate) {
        //if this is the schedule with publish notification it must be at or after sched time
        if (checkIfThisIsSchedulePushNotification() || !checkIfSchedulePushNotificationExists()) {
          if (
            new Date(publishDetails.scheduledPublishDate).getTime() >
            new Date(dateTimeCustom).getTime()
          ) {
            setHasDateSelectionError(true);
            setErrorMessageVariables({
              message:
                "You must select a date and time after the Scheduled Publish date",
              className: "invalid-date-time-selection-error",
            });
            return false;
          }

          //it must be after the sched time
        } else {
          if (
            new Date(publishDetails.scheduledPublishDate).getTime() >=
            new Date(dateTimeCustom).getTime()
          ) {
            setHasDateSelectionError(true);
            setErrorMessageVariables({
              message:
                "You must select a date and time after the Scheduled Publish date",
              className: "invalid-date-time-selection-error",
            });
            return false;
          }
        }
      }

      //must be before the archival date
      if (
        publishDetails.scheduledArchiveDate &&
        new Date(dateTimeCustom).getTime() >
          new Date(publishDetails.scheduledArchiveDate).getTime()
      ) {
        setHasDateSelectionError(true);
        setErrorMessageVariables({
          message: `This item is scheduled to be archived on ${formattedScheduledArchiveDateForErrorMessage}. Please select a date/time before this.`,
          className: "invalid-date-time-selection-error",
        });
        return false;
      }
      return true;
    }
  }

  function clearErrorMessages() {
    setShowErrorMessage(false);
    setHasDateSelectionError(false);
  }

  //if a radio selection is choose, this will ensure the date is valid
  //before setting state data
  useEffect(() => {
    if (customDateTime) {
      if (
        validateSelectedDateAndSetErrors(customDateTime) &&
        radioSelect &&
        radioSelect === ScheduleType.CHOOSE
      ) {
        setStoreDetails(ScheduleType.CHOOSE, customDateTime);
      } else if (radioSelect === ScheduleType.NOW) {
        setStoreDetails(ScheduleType.NOW, "NOW");
      } else if (radioSelect === ScheduleType.PUBLISH) {
        setStoreDetails(
          ScheduleType.PUBLISH,
          publishDetails.scheduledPublishDate
        );
      } else {
        setShowErrorMessage(true);
        setStepTwoValid(false);
      }
    }
  }, [customDateTime]);

  const setStoreDetails = (type: ScheduleType, datetime: string) => {
    setStore({
      ...store,
      scheduledPushTime: datetime,
      scheduleType: type,
    });
  };

  //checks validity of step 2
  useEffect(() => {
    if (
      !hasDateSelectionError &&
      !showErrorMessage &&
      ((radioSelect === ScheduleType.CHOOSE && customDateTime) ||
        radioSelect === ScheduleType.NOW ||
        radioSelect === ScheduleType.PUBLISH)
    ) {
      setStepTwoValid(true);
    } else {
      setStepTwoValid(false);
    }
  }, [store, radioSelect]);

  return (
    <div className={`schedule-notification-step`}>
      {hasMetNotificationLimit && (
        <ErrorMessage
          className="notification-limit-met"
          errorMessage="5/5 notifications scheduled. You can choose to send a notification immediately."
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          className={errorMessageVariables.className}
          errorMessage={errorMessageVariables.message}
        />
      )}

      <fieldset className="schedule-type-options">

        <legend className="schedule-type-question">
          When would you like to send the notification?
        </legend>

        <div className="schedule-type immediately">
          <input
            id="radio-immediately"
            className="radio-button"
            type="radio"
            value={ScheduleType.NOW}
            name="ScheduleType"
            onChange={handleRadioSelection}
            checked={
              radioSelect === ScheduleType.NOW || hasMetNotificationLimit
            }
            disabled={
              !publishDetails.isPublished || isRepublishingContentInFuture()
            }
            autoFocus
          />
          <label htmlFor="radio-immediately">Send Immediately</label>
        </div>
        {!disableScheduledPublish && !hasMetNotificationLimit && (
          <div className="schedule-type scheduled-publish">
            <input
              id="radio-publish"
              className="radio-button"
              type="radio"
              value={ScheduleType.PUBLISH}
              name="ScheduleType"
              checked={radioSelect === ScheduleType.PUBLISH}
              onChange={handleRadioSelection}
              disabled={!publishDetails.scheduledPublishDate}
            />
            <label htmlFor="radio-publish">{radioScheduledPublishCopy}</label>
          </div>
        )}
        {!hasMetNotificationLimit && (
          <div className="schedule-type choose-date">
            <input
              id="radio-choose"
              className="radio-button"
              type="radio"
              value={ScheduleType.CHOOSE}
              name="ScheduleType"
              checked={radioSelect === ScheduleType.CHOOSE}
              onChange={handleRadioSelection}
            />
            <label htmlFor="radio-choose">Choose a date and time</label>
          </div>
        )}

        {radioSelect === ScheduleType.CHOOSE && !hasMetNotificationLimit && (
          <div className="date-time-pickers" title="date-picker-div">
            <DatePicker
              selectedCalendarDate={selectedCalendarDate}
              setSelectedCalendarDate={setSelectedCalendarDate}
              minCalendarDate={defaultMinCalendarDate}
              hasError={hasDateSelectionError}
            />

            <BasicTimePicker
              selectedTime={selectedTime}
              setSelectedTime={setSelectedTime}
              hasError={hasDateSelectionError}
              defaultTime={defaultTime}
            />
          </div>
        )}
      </fieldset>
    </div>
  );
};

export default ScheduleStep;
